import React, { createContext, useContext, useMemo } from "react"

import { getFormattingLocale } from "shared/lib/locale/formattingLocale"

interface DateTimeContextValue {
  isoTimestamp: string
}

const DateTimeContext = createContext<DateTimeContextValue | null>(null)

function useDateTimeContext(): DateTimeContextValue {
  const ctx = useContext(DateTimeContext)
  if (!ctx)
    throw new Error(
      "DateTime.Date / DateTime.Time must be used inside <DateTime>"
    )
  return ctx
}

type DateTimeRootProps = Omit<
  React.TimeHTMLAttributes<HTMLTimeElement>,
  "dateTime"
> & {
  isoTimestamp: string
}

function DateTimeRoot({
  isoTimestamp,
  className,
  children,
  ...rest
}: DateTimeRootProps) {
  const ctx = useMemo(() => ({ isoTimestamp }), [isoTimestamp])
  return (
    <DateTimeContext.Provider value={ctx}>
      <time
        dateTime={isoTimestamp}
        className={["date-time", className].filter(Boolean).join(" ")}
        {...rest}
      >
        {children}
      </time>
    </DateTimeContext.Provider>
  )
}

type DateTimePartProps = React.HTMLAttributes<HTMLSpanElement> & {
  options?: Intl.DateTimeFormatOptions
}

function makeDateTimePart(
  defaultClass: string,
  defaultOptions: Intl.DateTimeFormatOptions
) {
  return function DateTimePart({
    options = defaultOptions,
    className,
    ...spanProps
  }: DateTimePartProps) {
    const { isoTimestamp } = useDateTimeContext()

    const formatted = useMemo(
      () =>
        new Intl.DateTimeFormat(getFormattingLocale(), options).format(
          new Date(isoTimestamp)
        ),
      [isoTimestamp, options]
    )

    return (
      <span
        className={[defaultClass, className].filter(Boolean).join(" ")}
        {...spanProps}
      >
        {formatted}
      </span>
    )
  }
}

export const DateTime = Object.assign(DateTimeRoot, {
  Date: makeDateTimePart("date-time__date", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }),
  Time: makeDateTimePart("date-time__time", {
    hour: "2-digit",
    minute: "2-digit"
  })
})
